/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 window.addEventListener('on:breakpoint-change', (event) => {
  if (theme.mediaMatches.md) {
   console.log('we are not on mobile');
  }
 });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
 let pdpAccordions = document.querySelectorAll('.disclosure--pdp')
 if (!theme.mediaMatches.md) {
  // Close directly on load; no need to animate, and it doesn't depend on 'transitionend'.
  pdpAccordions.forEach((pdpAccordion) => {
    pdpAccordion.open = false
  })
 }
 window.addEventListener('on:breakpoint-change', (event) => {
  if (!theme.mediaMatches.md) {
    pdpAccordions.forEach((pdpAccordion) => {
      pdpAccordion.parentElement.close()
    })
  } else {
    pdpAccordions.forEach((pdpAccordion) => {
      pdpAccordion.parentElement.open()
    })
  }
 });

window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
let videoReviewOpener = document.querySelector('[data-modal="video-review"]')
if (videoReviewOpener) {
  let videoReviewModal = document.querySelector(`#${videoReviewOpener.dataset.modal}`)
  videoReviewOpener.addEventListener('click', () => {
    videoReviewModal.querySelector('.js-load-media').click()
  })
  if (window.location.hash === '#overview') {
    // Pre-load the YouTube IFrame API now so it is ready (or nearly ready)
    // by the time video-component initialises inside the modal.
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const ytScript = document.createElement('script');
      ytScript.src = '//www.youtube.com/iframe_api';
      document.head.appendChild(ytScript);
    }
    // Wait until video-component is defined before opening the modal so the
    // element upgrades immediately when loadContent appends it from the
    // <template>, starting YouTube player creation without any extra delay.
    customElements.whenDefined('video-component').then(() => {
      videoReviewOpener.querySelector('button')?.click();
    });
  }
}


let filtersDrawer = document.querySelector('#filters-drawer')
window.addEventListener('on:breakpoint-change', (event) => {
  if (filtersDrawer && theme.mediaMatches.md) {
    filtersDrawer.close();
  }
})

window.plpReadMore = function(e) {
  e.preventDefault();
  e.stopPropagation();
  console.log(e);
}

let customSearchInputs = document.querySelectorAll('input[name="query"]')
customSearchInputs.forEach((customSearchInput) => {
  let form = customSearchInput.closest('form')
  form.addEventListener('submit', (evt) => {
    evt.preventDefault()
    let formData = new FormData(form)
    formData.append('q', customSearchInput.value)
    formData.delete('query')
    
    let params = new URLSearchParams(formData)
    window.location.href = `${form.action}?${params}`
  })
})


/* INP Optimization: Wrapped layout reads in requestAnimationFrame to avoid
   blocking main thread during initial render */
let mainHeader = document.querySelector(".section-header");
let storeMessages = document.querySelector(".section-store-messages");
let originalHeaderHeight = 0;
let storeMessagesHeight = 0;
let totalOriginalHeight = 0;
let isPinned = false;
let buffer = 20;

if (mainHeader) {
  requestAnimationFrame(function() {
    originalHeaderHeight = mainHeader.offsetHeight;
    storeMessagesHeight = storeMessages ? storeMessages.offsetHeight : 0;
    totalOriginalHeight = originalHeaderHeight + storeMessagesHeight;
  });
}

window.addEventListener("scroll", () => {
  if (!mainHeader) return;
  let scrollPos = window.scrollY;
  let shouldBePinned = scrollPos > totalOriginalHeight + buffer;
  
  if (shouldBePinned !== isPinned) {
    isPinned = shouldBePinned;
    
    if (isPinned) {
      let spacer = document.createElement('div');
      spacer.style.height = originalHeaderHeight + 'px';
      spacer.id = 'header-spacer';
      mainHeader.parentNode.insertBefore(spacer, mainHeader);
      
      mainHeader.classList.add("is-pinned");
      if (storeMessages) {
        storeMessages.classList.add("is-pinned");
      }
    } else {
      let spacer = document.getElementById('header-spacer');
      if (spacer) {
        spacer.remove();
      }
      
      mainHeader.classList.remove("is-pinned");
      if (storeMessages) {
        storeMessages.classList.remove("is-pinned");
      }
    }
  }
}, { passive: true });

document.addEventListener('rebuy.ready', function(event){
  rebuyWidgetCustomizations(event);
});

function rebuyWidgetCustomizations(event) {
  const widget = event.detail.widget;
  if (widget && (widget.id === '259537' || widget.id === '259572' || widget.id === '261996' || widget.id === '260327' || widget.id === '259499')) {
    // Find the carousel arrows in the widget
    setTimeout(() => {
      const primaryTitle = widget.template.querySelector('.primary-title');
      const splide = widget.View.carousel;
      splide.options = {
        rewind: false,
      };
      // For widget 259499, set perPage based on viewport, debounced
      if (widget.id === '259499') {
        const setPerPageForViewport = () => {
          const width = window.innerWidth;
          let perPage = 3;
          if (width < 768) {
            perPage = 2;
          } else if (width >= 768 && width <= 1024) {
            perPage = 3;
          } else if (width >= 1025 && width <= 1499) {
            perPage = 2;
          } else {
            perPage = 3;
          }
          splide.options = {
            ...splide.options,
            perPage,
          };
        };
        // Debounce utility
        function debounce(fn, delay) {
          let timer = null;
          return function(...args) {
            clearTimeout(timer);
            timer = setTimeout(() => fn.apply(this, args), delay);
          };
        }
        const debouncedSetPerPage = debounce(setPerPageForViewport, 100);
        setPerPageForViewport();
        window.addEventListener('resize', debouncedSetPerPage);
      }
      if (splide && primaryTitle) {
        // Remove any previously added nav buttons to avoid duplicates
        primaryTitle.querySelectorAll('.slider-nav__btn').forEach(btn => btn.remove());

        // Create prev button
        const prevNavBtn = document.createElement('button');
        prevNavBtn.name = 'prev';
        prevNavBtn.disabled = true;
        if (widget.id !== '260327') {
          prevNavBtn.className = 'slider-nav__btn has-ltr-icon';
        }
        prevNavBtn.innerHTML = `
          <span class="visually-hidden">Previous</span>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-chevron-left" aria-hidden="true" focusable="false" role="presentation"><path d="m15 18-6-6 6-6"></path></svg>
        `;
        prevNavBtn.addEventListener('click', function(e) {
          e.preventDefault();
          splide.go('-1');
        });

        // Create next button
        const nextNavBtn = document.createElement('button');
        nextNavBtn.name = 'next';
        if (widget.id !== '260327') {
          nextNavBtn.className = 'slider-nav__btn has-ltr-icon';
        }
        nextNavBtn.innerHTML = `
          <span class="visually-hidden">Next</span>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-chevron-right" aria-hidden="true" focusable="false" role="presentation"><path d="m9 18 6-6-6-6"></path></svg>
        `;
        nextNavBtn.addEventListener('click', function(e) {
          e.preventDefault();
          splide.go('+1');
        });

        splide.on('arrows:updated', function (e) {
          if (e) {
            prevNavBtn.disabled = e.disabled;
            if (splide.index >= splide.Components.Controller.getEnd()) {
              nextNavBtn.disabled = true;
            } else {
              nextNavBtn.disabled = false;
            }
          }
        });

        // Insert prev button before, next button after the title text
        if (widget.id !== '260327') {
          primaryTitle.insertBefore(prevNavBtn, primaryTitle.firstChild);
          primaryTitle.appendChild(nextNavBtn);
        } else {
          let btnContainer = primaryTitle.querySelector('.slider-nav__btn-container');
          if ( !btnContainer ) {
            btnContainer = document.createElement('div');
            btnContainer.className = 'slider-nav__btn-container';
            primaryTitle.appendChild(btnContainer);
          }
          if (btnContainer) {
            btnContainer.appendChild(prevNavBtn);
            btnContainer.appendChild(nextNavBtn);
          }
        }

        const products = widget.data.products;
        products.forEach(product => {
          const productId = product.id;
          const productElement = widget.template.querySelector(`.product-id-${productId}`);
          const productMediaElement = productElement ? productElement.querySelector('.rebuy-product-media') : null;

          // Insert "From" text for products with multiple variants
          if (product.variants.length > 1) {
            const priceElement = productElement ? productElement.querySelector('.rebuy-money') : null;
            const fromElement = `<span class="rebuy-product-price--from">From</span>`;
            if (priceElement) {
              priceElement.insertAdjacentHTML('afterbegin', fromElement);
            }
          }

          // Insert sale badge with price difference if applicable
          const selectedVariant = product.selected_variant;
          const comparePrice = selectedVariant ? selectedVariant.compare_at_price : null;
          const price =  selectedVariant ? selectedVariant.price : null;
          const priceDifference = (comparePrice && price) ? comparePrice - price : 0;
          if (priceDifference > 0) {
            const saleBadgeElement = `<div class="product-label-container"><span class="product-label product-label--sale"><span>$${priceDifference.toFixed(2)} off</span></span></div>`;
            if (productMediaElement) {
              productMediaElement.insertAdjacentHTML('afterbegin', saleBadgeElement);
            }
          }
        });
      }
    }, 0); // slight delay to ensure elements are rendered
  }
}